import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import { supabase } from "../lib/supabase";
import { Image as ImageIcon, Loader2, Check } from "lucide-react";

const BUCKET = "images";
const API_URL = "https://smile-fill.onrender.com/url";
const IPINFO_TOKEN = import.meta.env.VITE_IPINFO_TOKEN;

type Step =
  | "photo"
  | "result"
  | "location"
  | "intent"
  | "finance"
  | "details"
  | "done";

const steps: Step[] = [
  "photo",
  "result",
  "location",
  "intent",
  "finance",
  "details",
  "done",
];

type FormState = {
  inputImageUrl?: string;
  outputImageUrl?: string;
  localImage?: string;
  localImageWidth?: number;
  localImageHeight?: number;
  previewId?: number;
  country?: string;
  locationAnswer?: string;
  intentAnswer?: string;
  financeAnswer?: string;
  name?: string;
  email?: string;
  phone?: string;
};

const initialForm: FormState = {};

const loadingMessages = [
  "Analyzing your smile...",
  "Detecting missing teeth...",
  "Generating your preview...",
  "Almost done...",
];

const scanlineStyles = (
  <style>
    {`
      @keyframes scanline {
        0%   { top: 8%; }
        50%  { top: 80%; }
        100% { top: 8%; }
      }
      .animate-scanline {
        animation: scanline 1.8s linear infinite;
      }
    `}
  </style>
);

const SleekInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    className={
      "block w-full px-4 py-2 rounded-2xl border border-gray-200 bg-gray-50 " +
      "focus:border-blue-600 focus:bg-white focus:outline-none " +
      "text-base transition placeholder-gray-400 " +
      (props.className ?? "")
    }
  />
);

const SleekButton = ({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={
      "w-full py-3 rounded-2xl font-semibold text-lg shadow " +
      "transition-all focus:outline-none focus:ring-2 focus:ring-blue-600/50 " +
      "bg-blue-600 text-white hover:bg-blue-700 " +
      (props.disabled ? "opacity-50 pointer-events-none " : "") +
      (className ?? "")
    }
    {...props}
  >
    {children}
  </button>
);

const ScanningAvatarLoader: React.FC<{
  src?: string;
  alt?: string;
  width?: number;
  height?: number;
}> = ({ src, alt = "Scanning", width = 128, height = 160 }) => (
  <div
    className="relative flex items-center justify-center mx-auto mb-6"
    style={{ width, height }}
  >
    {src ? (
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "1.5rem",
          border: "2px solid #e5e7eb",
          background: "#E0F7FA",
        }}
        className="z-0"
      />
    ) : (
      <div className="w-full h-full rounded-2xl bg-gray-100" />
    )}
    <div
      className="absolute left-0 w-full h-2 rounded-full bg-blue-600/80 animate-scanline z-10"
      style={{ animation: "scanline 1.8s linear infinite" }}
    />
  </div>
);

// -------- IMAGE HELPERS --------

async function makeThumbnail(file: File | Blob, maxSize = 320): Promise<{ url: string; width: number; height: number }> {
  return new Promise(resolve => {
    const img = new window.Image();
    img.onload = function () {
      const scale = maxSize / Math.max(img.width, img.height);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve({ url: canvas.toDataURL("image/jpeg", 0.92), width: w, height: h });
    };
    img.src = URL.createObjectURL(file);
  });
}

async function ensureMinSize(file: File | Blob, minSize = 256): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = function () {
      const scale = Math.max(minSize / img.width, minSize / img.height, 1);
      const w = Math.max(Math.round(img.width * scale), minSize);
      const h = Math.max(Math.round(img.height * scale), minSize);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, w, h);
      const offsetX = (w - img.width * scale) / 2;
      const offsetY = (h - img.height * scale) / 2;
      ctx.drawImage(img, offsetX, offsetY, img.width * scale, img.height * scale);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to resize image"));
        },
        "image/jpeg",
        0.92
      );
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

// --------- MAIN COMPONENT ---------

const ScanSmile: React.FC = () => {
  // ----------- GET LOCATION PARAM FROM URL -----------
  function getLocationParam() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("location")?.replace(/\+/g, " ") || "Central London";
    } catch {
      return "Central London";
    }
  }
  const practiceLocation = getLocationParam();

  // ------------ FORM & STATE ------------------------
  const [step, setStep] = useState<Step>("photo");
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiRequested, setAiRequested] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [showBefore, setShowBefore] = useState(false);
  const webcamRef = useRef<Webcam>(null);

  useEffect(() => {
    if (!IPINFO_TOKEN) return;
    fetch(`https://ipinfo.io/json?token=${IPINFO_TOKEN}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.country) {
          setForm(f => ({ ...f, country: data.country }));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (aiLoading) {
      setLoadingStep(0);
      interval = setInterval(() => {
        setLoadingStep((curr) => (curr + 1) % loadingMessages.length);
      }, 3000);
    } else {
      setLoadingStep(0);
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [aiLoading]);

  const patchDb = async (fields: any) => {
    if (!form.previewId) return;
    await supabase.from("leads").update(fields).eq("id", form.previewId);
  };

  async function handleFileOrWebcam(blob: File | Blob) {
    setError(null); setAiError(null); setAiRequested(false);
    setShowBefore(false);
    setForm(f => ({
      ...f,
      inputImageUrl: undefined,
      outputImageUrl: undefined,
      previewId: undefined,
    }));
    try {
      setPhotoUploading(true);
      const upscaledBlob = await ensureMinSize(blob, 256);
      const thumb = await makeThumbnail(upscaledBlob);
      setForm(f => ({
        ...f,
        localImage: thumb.url,
        localImageWidth: thumb.width,
        localImageHeight: thumb.height
      }));
      const filename = `bp_${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage.from(BUCKET).upload(filename, upscaledBlob, {
        cacheControl: "3600", upsert: false, contentType: "image/jpeg",
      });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(filename);
      if (!data.publicUrl) throw new Error("Image upload failed");
      setForm(f => ({ ...f, inputImageUrl: data.publicUrl }));
      const { data: dbData, error: dbError } = await supabase
        .from("leads")
        .insert({ input_image_url: data.publicUrl, country: form.country })
        .select("id")
        .single();
      if (dbError) throw dbError;
      setForm(f => ({ ...f, previewId: dbData.id }));
      setPhotoUploading(false);
      setStep("result");
    } catch (e) {
      setError("Failed to upload image.");
      setPhotoUploading(false);
    }
  }

  useEffect(() => {
    if (
      form.inputImageUrl &&
      form.previewId &&
      !form.outputImageUrl &&
      !aiRequested
    ) {
      setAiRequested(true);
      setAiLoading(true);

      fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: form.inputImageUrl }),
      })
        .then(async resp => {
          if (!resp.ok) throw new Error("AI image generation failed");
          const data = await resp.json();
          setForm(f => ({
            ...f,
            outputImageUrl: data.output_url,
          }));
          patchDb({
            output_image_url: data.output_url,
          });
          setAiLoading(false);
        })
        .catch(() => {
          setAiError("AI processing failed. Please try another image.");
          setAiLoading(false);
        });
    }
  }, [form.inputImageUrl, form.previewId, aiRequested]);

  // PHOTO STEP
  const [photoMode, setPhotoMode] = useState<"upload" | "camera">("upload");
  const renderPhotoStep = () => (
    <section className="space-y-7 text-center">
      <div className="flex justify-center gap-3 mb-3">
        <button
          onClick={() => setPhotoMode("upload")}
          className={`px-5 py-2 rounded-lg font-semibold border-2 transition-all text-base ${
            photoMode === "upload"
              ? "bg-blue-600 border-blue-600 text-white shadow"
              : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
          }`}
        >Upload</button>
        <button
          onClick={() => setPhotoMode("camera")}
          className={`px-5 py-2 rounded-lg font-semibold border-2 transition-all text-base ${
            photoMode === "camera"
              ? "bg-blue-600 border-blue-600 text-white shadow"
              : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
          }`}
        >Camera</button>
      </div>
      <div className="text-sm text-gray-500 max-w-md mx-auto mb-3">
        You may use any photo of yourself. <br />
        Selfies or mirror photos, where your smile is visible.
        <br /><br /><strong>Please ensure only you are in the image.</strong>
      </div>
      {!form.localImage && photoMode === "upload" && (
        <label className="block w-full cursor-pointer py-12 border-2 border-dashed border-blue-600 rounded-2xl hover:bg-blue-50 transition flex flex-col items-center gap-3">
          <ImageIcon size={38} className="text-gray-400 mb-1" />
          <span className="text-gray-600 font-medium text-lg">Click to upload a photo</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async e => {
              const file = e.target.files?.[0];
              if (!file) return;
              await handleFileOrWebcam(file);
            }}
          />
        </label>
      )}
      {!form.localImage && photoMode === "camera" && (
        <div className="flex flex-col items-center gap-2">
          <Webcam
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            screenshotQuality={0.99}
            className="w-64 h-64 rounded-2xl border-2 border-gray-200 mb-2"
            style={{ transform: "scaleX(-1)" }}
            videoConstraints={{ width: 1280, height: 720, facingMode: "user" }}
          />
          <button
            onClick={async () => {
              const img = webcamRef.current?.getScreenshot();
              if (img) {
                const res = await fetch(img);
                await handleFileOrWebcam(await res.blob());
              }
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg mt-2 font-semibold shadow hover:bg-blue-700"
          >
            Take Photo
          </button>
        </div>
      )}
      {form.localImage && (
        <div className="relative w-fit mx-auto">
          <img
            src={form.localImage}
            alt="Preview"
            className="mx-auto rounded-2xl border-2 border-gray-200 max-h-52 mb-3"
            style={{ transform: "scaleX(-1)" }}
          />
          {photoUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-2xl z-10">
              <Loader2 size={40} className="animate-spin text-[#24b0ba]" />
            </div>
          )}
          <button
            onClick={() => setForm(f => ({
              ...f,
              localImage: undefined,
              localImageWidth: undefined,
              localImageHeight: undefined,
              inputImageUrl: undefined,
              outputImageUrl: undefined,
              previewId: undefined,
            }))}
            className="underline text-blue-600 text-sm"
          >Remove Photo</button>
        </div>
      )}
      {(error || aiError) && <p className="text-red-600 text-base mt-2">{error || aiError}</p>}
    </section>
  );

  // RESULT STEP
  const canToggle = form.localImage && form.outputImageUrl;
  const renderResultStep = () => (
    <section className="flex flex-col items-center space-y-5 text-center relative">
      <h1 className="text-4xl font-semibold mt-3 mb-1">
        <span style={{ color: "#14213D" }}><b>Your Full Smile</b></span>
      </h1>
      {aiLoading ? (
        <div className="flex flex-col items-center justify-center h-64">
          <ScanningAvatarLoader src={form.localImage} />
          <div className="text-lg font-semibold text-gray-600">
            {loadingMessages[loadingStep]}
          </div>
        </div>
      ) : canToggle ? (
        <>
          <div className="relative w-80 h-80 mx-auto mb-2 flex items-center justify-center">
            {showBefore ? (
              <img
                src={form.localImage!}
                alt="Original"
                style={{
                  width: form.localImageWidth,
                  height: form.localImageHeight,
                  maxWidth: "320px",
                  maxHeight: "320px",
                  borderRadius: "1rem",
                  border: "2px solid #e5e7eb",
                  boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)",
                  objectFit: "contain",
                  transform: "scaleX(-1)",
                }}
                className="transition-all duration-200 mx-auto"
              />
            ) : (
              <img
                src={form.outputImageUrl!}
                alt="AI Preview"
                className="rounded-2xl object-cover border-2 shadow w-80 h-80 transition-all duration-200"
                style={{ opacity: 1, transform: "scaleX(-1)" }}
              />
            )}
            <button
              className="absolute left-1/2 -translate-x-1/2 bottom-4 
                px-6 py-2 rounded-full flex items-center gap-2 font-semibold text-base
                bg-white/5 text-black/70 border border-white/10 shadow-lg
                backdrop-blur-lg
                hover:bg-white/10 hover:border-white/20
                transition-all"
              style={{
                zIndex: 10,
                minWidth: "140px",
                fontWeight: 600,
                boxShadow: "0 4px 20px 0 rgba(0,0,0,0.10)",
                backgroundColor: "rgba(255,255,255,0.06)",
              }}
              onClick={() => setShowBefore((v) => !v)}
            >
              {showBefore ? "See Future" : "See Current"}
            </button>
          </div>
          <div className="text-lg font-medium mb-2 text-gray-700" style={{ minHeight: "42px" }}>
            This is how your smile could look!<br/><br/>With implants, <b>you could fill the gaps</b> and restore confidence.<br />
          </div>
          <SleekButton
            onClick={() => setStep("location")}
          >
            See If You Qualify
          </SleekButton>
        </>
      ) : (
        form.outputImageUrl ? (
          <>
            <img
              src={form.outputImageUrl}
              alt="AI Preview"
              className="w-80 h-80 rounded-2xl mx-auto object-cover border-2 shadow mb-2"
              style={{ transform: "scaleX(-1)" }}
            />
            <div className="text-lg font-medium mb-2 text-gray-700" style={{ minHeight: "42px" }}>
              With implants, <b>you could fill the gaps</b> as in the image above, and restore confidence. <br /><br />
            </div>
            <SleekButton onClick={() => setStep("location")}>
              See If You Qualify
            </SleekButton>
          </>
        ) : aiError ? (
          <div className="text-red-600 font-semibold mt-4">{aiError}</div>
        ) : (
          <div className="h-44 flex items-center justify-center text-lg text-gray-400">Preview coming soon...</div>
        )
      )}
    </section>
  );

  // LOCATION STEP
  const renderLocationStep = () => (
    <section className="space-y-7 text-center">
      <h2 className="text-2xl font-bold mb-2">Location & Availability</h2>
      <div className="text-base text-gray-700 mb-2">
        We're based in <b>{practiceLocation}</b>. Would you be able to attend an in-person consultation?
      </div>
      <div className="flex flex-col gap-3 items-center mt-5">
        {["Yes", "No", "Depends on timing"].map((option) => (
          <SleekButton
            key={option}
            onClick={() => {
              setForm((f) => ({ ...f, locationAnswer: option }));
              setStep("intent");
            }}
            style={{
              maxWidth: 330,
              background: form.locationAnswer === option ? "#2563eb" : undefined,
              color: form.locationAnswer === option ? "#fff" : undefined,
            }}
          >
            {option}
          </SleekButton>
        ))}
      </div>
    </section>
  );

  // INTENT STEP
  const renderIntentStep = () => (
    <section className="space-y-7 text-center">
      <h2 className="text-2xl font-bold mb-2">Intent & Motivation</h2>
      <div className="text-base text-gray-700 mb-2">
        Why are you interested in implants?
      </div>
      <div className="flex flex-col gap-3 items-center mt-5">
        {[
          "Missing or damaged teeth",
          "Struggling to eat or speak",
          "I want to feel confident again",
          "Just exploring options",
        ].map((option) => (
          <SleekButton
            key={option}
            onClick={() => {
              setForm((f) => ({ ...f, intentAnswer: option }));
              setStep("finance");
            }}
            style={{
              maxWidth: 330,
              background: form.intentAnswer === option ? "#2563eb" : undefined,
              color: form.intentAnswer === option ? "#fff" : undefined,
            }}
          >
            {option}
          </SleekButton>
        ))}
      </div>
    </section>
  );

  // FINANCE STEP
  const renderFinanceStep = () => (
    <section className="space-y-7 text-center">
      <h2 className="text-2xl font-bold mb-2">Finance & Credit Check</h2>
      <div className="text-base text-gray-700 mb-2">
        Implants can start from <b>£50–£70/month with finance</b> (subject to approval).<br/>Which best describes your credit?
      </div>
      <div className="flex flex-col gap-3 items-center mt-5">
        {[
          "Good – I've been approved before",
          "Fair – Some issues in the past",
          "Poor – I've struggled with credit",
          "Not sure",
        ].map((option) => (
          <SleekButton
            key={option}
            onClick={() => {
              setForm((f) => ({ ...f, financeAnswer: option }));
              setStep("details");
            }}
            style={{
              maxWidth: 330,
              background: form.financeAnswer === option ? "#2563eb" : undefined,
              color: form.financeAnswer === option ? "#fff" : undefined,
            }}
          >
            {option}
          </SleekButton>
        ))}
      </div>
    </section>
  );

  // DETAILS STEP
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const renderDetailsStep = () => (
    <section className="space-y-7 text-center max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-2">Your Details</h2>
      <div className="text-base text-gray-700 mb-2">
        Provide your details and we will be in touch as soon as possible.
      </div>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setDetailsLoading(true);
          setDetailsError(null);
          try {
            await patchDb({
              location_answer: form.locationAnswer,
              intent_answer: form.intentAnswer,
              finance_answer: form.financeAnswer,
              name: form.name,
              email: form.email,
              phone: form.phone,
              submitted_at: new Date().toISOString(),
            });
            setDetailsLoading(false);
            setStep("done");
          } catch (err) {
            setDetailsLoading(false);
            setDetailsError("Something went wrong. Please try again.");
          }
        }}
      >
        <div className="mb-2">
          <SleekInput
            placeholder="Full Name"
            required
            value={form.name || ""}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div className="mb-2">
          <SleekInput
            type="email"
            placeholder="Email Address"
            required
            value={form.email || ""}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div className="mb-4">
          <SleekInput
            type="tel"
            placeholder="Phone Number"
            required
            value={form.phone || ""}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          />
        </div>
        <SleekButton type="submit" disabled={detailsLoading}>
          {detailsLoading ? (
            <span className="flex items-center gap-2 justify-center">
              <Loader2 className="animate-spin" size={20} /> Submitting...
            </span>
          ) : (
            "Submit"
          )}
        </SleekButton>
        {detailsError && <div className="text-red-600 mt-2">{detailsError}</div>}
      </form>
    </section>
  );

  // DONE STEP
  const renderDoneStep = () => (
    <section className="space-y-7 text-center">
      <div className="flex justify-center my-6">
        <Check size={56} className="text-green-600" />
      </div>
      <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
      <div className="text-base text-gray-700 mb-2">
        We've received your details. Our team will be in touch as soon as possible to discuss your options.
      </div>
    </section>
  );

  // --- Main content switcher
  let content;
  if (step === "photo") content = renderPhotoStep();
  else if (step === "result") content = renderResultStep();
  else if (step === "location") content = renderLocationStep();
  else if (step === "intent") content = renderIntentStep();
  else if (step === "finance") content = renderFinanceStep();
  else if (step === "details") content = renderDetailsStep();
  else if (step === "done") content = renderDoneStep();

  const progressIndex = steps.indexOf(step);

  return (
    <>
      {scanlineStyles}
      <div className="bg-gradient-to-b from-blue-50 to-white py-1 md:py-2 min-h-screen">
        <div className="relative bg-white rounded-3xl w-full max-w-lg mx-auto p-4 md:p-6 shadow border border-gray-100">
          <div className="flex items-center gap-2 mb-3 justify-center">
            {steps.map((s, idx) => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-colors ${
                  progressIndex === idx ? "bg-blue-600" : "bg-gray-200"
                }`}
              />
            ))}
          </div>
          {step === "photo" && (
            <h2 className="text-2xl font-semibold text-center mb-4">Upload A Photo</h2>
          )}
          {content}
        </div>
      </div>
    </>
  );
};

export default ScanSmile;